import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

export const blue = '#0d4efe';
export const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';

const STAGE_COLOR_BY_ID = {
  'not-contacted': '#64748b',
  'reached-out': '#0d4efe',
  'in-conversation': '#7c3aed',
  'demo-scheduled': '#f59e0b',
  onboarded: '#00a37a',
  'not-interested': '#d3455b',
};

/** Colors for admin-created stages that are not in the seed defaults. */
const CUSTOM_STAGE_COLORS = [
  '#0891b2',
  '#c026d3',
  '#ea580c',
  '#4d7c0f',
  '#9333ea',
  '#0f766e',
];

/** Stable color per pipeline stage id so rows and KPI cards stay consistent. */
export function stageColor(stageId) {
  if (!stageId) return muted;
  if (STAGE_COLOR_BY_ID[stageId]) return STAGE_COLOR_BY_ID[stageId];
  let hash = 0;
  for (let i = 0; i < stageId.length; i += 1) {
    hash = (hash * 31 + stageId.charCodeAt(i)) % 9973;
  }
  return CUSTOM_STAGE_COLORS[hash % CUSTOM_STAGE_COLORS.length];
}

function hexToRgba(hex, alpha) {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function stageChipSx(stageId) {
  const color = stageColor(stageId);
  return {
    fontWeight: 700,
    color,
    backgroundColor: hexToRgba(color, 0.12),
    border: `1px solid ${hexToRgba(color, 0.32)}`,
  };
}

export function stageKpiCardSx(stageId, selected = false) {
  const color = stageColor(stageId);
  return {
    cursor: 'pointer',
    borderColor: selected ? color : hexToRgba(color, 0.35),
    borderWidth: selected ? 2 : 1,
    boxShadow: selected
      ? `0 0 0 3px ${hexToRgba(color, 0.2)}, 0 4px 18px rgba(26, 26, 46, 0.08)`
      : '0 4px 18px rgba(26, 26, 46, 0.06)',
    background: selected
      ? `linear-gradient(135deg, ${hexToRgba(color, 0.22)} 0%, #ffffff 55%, ${hexToRgba(color, 0.1)} 100%)`
      : `linear-gradient(135deg, ${hexToRgba(color, 0.12)} 0%, #ffffff 60%, ${hexToRgba(color, 0.05)} 100%)`,
    transform: selected ? 'translateY(-1px)' : 'none',
    transition:
      'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease, background 0.15s ease',
    '&:hover': {
      borderColor: color,
      boxShadow: `0 0 0 3px ${hexToRgba(color, 0.16)}, 0 6px 20px rgba(26, 26, 46, 0.1)`,
    },
    '& .crm-kpi-value': { color },
    '& .crm-kpi-percent': { color },
  };
}

export function totalKpiCardSx(selected = false) {
  return {
    cursor: 'pointer',
    borderColor: selected ? blue : 'rgba(13, 78, 254, 0.12)',
    borderWidth: selected ? 2 : 1,
    boxShadow: selected
      ? `0 0 0 3px rgba(13, 78, 254, 0.18), 0 4px 18px rgba(26, 26, 46, 0.08)`
      : '0 4px 18px rgba(26, 26, 46, 0.06)',
    transform: selected ? 'translateY(-1px)' : 'none',
    transition:
      'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
    '&:hover': {
      borderColor: blue,
      boxShadow: '0 0 0 3px rgba(13, 78, 254, 0.14), 0 6px 20px rgba(26, 26, 46, 0.1)',
    },
  };
}

export const CrmToolbar = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
  marginBottom: 20,
}));

export const CrmKpiRow = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 14,
  marginBottom: 20,
}));

export const CrmKpiCard = styled(Paper)(() => ({
  padding: '16px 18px',
  borderRadius: 14,
  border: '1px solid rgba(13, 78, 254, 0.12)',
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.08) 0%, rgba(255, 255, 255, 1) 45%, rgba(13, 78, 254, 0.04) 100%)',
  boxShadow: '0 4px 18px rgba(26, 26, 46, 0.06)',
}));

export const CrmKpiValue = styled(Typography)(() => ({
  fontFamily: "'Exo 2', sans-serif",
  fontWeight: 700,
  fontSize: '1.65rem',
  lineHeight: 1.1,
  color: ink,
}));

export const CrmKpiLabel = styled(Typography)(() => ({
  color: muted,
  fontWeight: 600,
  fontSize: '0.8rem',
  marginTop: 4,
}));

export const CrmKpiPercent = styled(Typography)(() => ({
  color: blue,
  fontWeight: 700,
  fontSize: '0.85rem',
  marginTop: 2,
}));

export const CrmTableWrap = styled(Box)(() => ({
  overflowX: 'auto',
  borderRadius: 12,
  border: '1px solid rgba(0, 0, 0, 0.08)',
  background: '#fff',
}));

export const CrmTable = styled('table')(() => ({
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
  '& th': {
    textAlign: 'left',
    padding: '8px 10px',
    background: 'rgba(247, 250, 252, 0.95)',
    color: muted,
    fontWeight: 700,
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  '& td': {
    padding: '12px 14px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    color: ink,
    verticalAlign: 'top',
  },
}));

export const CrmSortHeaderButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  margin: 0,
  padding: '4px 6px',
  border: 'none',
  borderRadius: 8,
  background: active ? 'rgba(13, 78, 254, 0.08)' : 'transparent',
  color: active ? blue : muted,
  font: 'inherit',
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'background 0.15s ease, color 0.15s ease',
  '&:hover': {
    background: 'rgba(13, 78, 254, 0.1)',
    color: blue,
  },
  '& .crm-sort-icon': {
    fontSize: 16,
    opacity: active ? 1 : 0.45,
  },
}));

export const CrmTableRow = styled('tr', {
  shouldForwardProp: (prop) => prop !== 'stageId',
})(({ stageId }) => {
  const color = stageColor(stageId);
  return {
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    backgroundColor: hexToRgba(color, 0.05),
    '& > td:first-of-type': {
      boxShadow: `inset 4px 0 0 ${color}`,
    },
    '&:hover': {
      backgroundColor: hexToRgba(color, 0.13),
    },
  };
});

export const CrmPrimaryButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 10,
  color: '#fff',
  background: 'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    background: 'linear-gradient(90deg, #01d4a0 0%, #0b45e0 100%)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.7)',
  },
}));

export const CrmSecondaryButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  color: ink,
  borderColor: 'rgba(13, 78, 254, 0.35)',
  '&:hover': {
    borderColor: blue,
    background: 'rgba(13, 78, 254, 0.04)',
  },
}));

export const StyledCrmDialog = styled(Dialog)(() => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(26, 26, 46, 0.52)',
    backdropFilter: 'blur(4px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgba(13, 78, 254, 0.14)',
    boxShadow:
      '0 24px 56px rgba(13, 78, 254, 0.16), 0 8px 24px rgba(0, 0, 0, 0.06)',
    background: '#ffffff',
    width: '100%',
  },
}));

export const CrmDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75),
  margin: 0,
  padding: theme.spacing(2.5, 3),
  paddingRight: theme.spacing(6),
  position: 'relative',
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.14) 0%, rgba(13, 78, 254, 0.1) 55%, rgba(13, 78, 254, 0.04) 100%)',
  borderBottom: '1px solid rgba(13, 78, 254, 0.1)',
  fontWeight: 700,
  fontSize: '1.125rem',
  color: ink,
  '& .crm-dialog-icon-wrap': {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background:
      'linear-gradient(145deg, rgba(1, 235, 178, 0.2) 0%, rgba(13, 78, 254, 0.15) 100%)',
    border: '1px solid rgba(13, 78, 254, 0.18)',
  },
}));

export const CrmDialogCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: muted,
  borderRadius: 10,
  '&:hover': {
    background: 'rgba(13, 78, 254, 0.08)',
    color: ink,
  },
}));

export const CrmDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  background: '#f7fafc',
}));

export const CrmDialogActions = styled(DialogActions)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2, 3, 2.5),
  gap: theme.spacing(1.25),
  background:
    'linear-gradient(180deg, rgba(247, 250, 252, 0.95) 0%, rgba(240, 244, 248, 0.98) 100%)',
  borderTop: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const CrmField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    background: '#fff',
  },
}));

export const CrmPanel = styled(Box)(() => ({
  padding: 16,
  borderRadius: 12,
  background: '#fff',
  border: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const CrmSectionTitle = styled(Typography)(() => ({
  fontWeight: 700,
  color: ink,
  marginBottom: 8,
}));
